import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { fmtMoney, fmtNumber } from "@/lib/fmt";
import { Download } from "lucide-react";

function defaultCycle() {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q}-${d.getFullYear()}`;
}

export default function AdminRoyalty() {
  const [cycle, setCycle] = useState(defaultCycle());
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/royalty?cycle=${cycle}`);
    setReport(data);
  }, [cycle]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const url = `${API}/admin/royalty.csv?cycle=${cycle}`;
    // Open in a new tab — axios+cookies handles auth via httpOnly cookie automatically.
    window.location.href = url;
  };

  const cycles = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) {
    for (let q = 4; q >= 1; q--) cycles.push(`Q${q}-${y}`);
  }

  return (
    <div className="space-y-10" data-testid="admin-royalty-page">
      <header className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="hh-overline mb-3">Admin · Royalty Report</p>
          <h1 className="font-serif text-4xl text-hh-ink tracking-tight">Quarterly royalty calculation</h1>
        </div>
        <div className="flex items-center gap-3">
          <select value={cycle} onChange={(e) => setCycle(e.target.value)} data-testid="cycle-select"
            className="px-4 py-2.5 rounded-lg border border-hh-border bg-white text-hh-ink">
            {cycles.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={downloadCsv} className="hh-btn-primary" data-testid="download-csv-btn">
            <Download size={16} className="mr-2" /> Download CSV
          </button>
        </div>
      </header>

      {!report ? (
        <div className="text-hh-ink2">Loading…</div>
      ) : (
        <>
          <div className="hh-card p-6 flex flex-wrap gap-x-12 gap-y-4">
            <div><div className="hh-overline mb-1">Cycle</div><div className="font-serif text-2xl text-hh-ink">{report.cycle}</div></div>
            <div><div className="hh-overline mb-1">Period</div><div className="font-serif text-2xl text-hh-ink">{report.period_start} → {report.period_end}</div></div>
            <div><div className="hh-overline mb-1">Authors with sales</div>
              <div className="font-serif text-2xl text-hh-ink">{report.authors.filter(a => a.total_units > 0).length} / {report.authors.length}</div></div>
          </div>

          <div className="space-y-6">
            {report.authors.filter(a => a.total_units > 0).map((a) => (
              <div key={a.author_id} className="hh-card p-6" data-testid={`royalty-${a.author_id}`}>
                <div className="flex flex-wrap items-center justify-between mb-4">
                  <div>
                    <h3 className="font-serif text-xl text-hh-ink">{a.author_name}</h3>
                    <p className="text-xs text-hh-ink2 font-mono">{a.email}</p>
                  </div>
                  <div className="text-right">
                    <div className="hh-overline mb-1">Royalty %</div>
                    <div className="font-serif text-xl text-hh-primary">{a.royalty_pct}%</div>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-left text-hh-ink2">
                    <tr>
                      <th className="py-2 pr-6 hh-overline">Currency</th>
                      <th className="py-2 pr-6 hh-overline">Units</th>
                      <th className="py-2 pr-6 hh-overline">Net pub comp</th>
                      <th className="py-2 pr-6 hh-overline">Author share ({a.royalty_pct}%)</th>
                      <th className="py-2 pr-6 hh-overline">Publisher (5%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(a.by_currency).map(([ccy, v]) => (
                      <tr key={ccy} className="border-t border-hh-border">
                        <td className="py-3 pr-6 text-hh-ink font-medium">{ccy}</td>
                        <td className="py-3 pr-6 text-hh-ink">{fmtNumber(v.units)}</td>
                        <td className="py-3 pr-6 text-hh-ink2">{fmtMoney(v.net_pub_comp, ccy)}</td>
                        <td className="py-3 pr-6 text-hh-ink font-medium">{fmtMoney(v.author_share, ccy)}</td>
                        <td className="py-3 pr-6 text-hh-ink2">{fmtMoney(v.publisher_share, ccy)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            {report.authors.filter(a => a.total_units > 0).length === 0 && (
              <div className="hh-card p-12 text-center text-hh-ink2">
                No sales recorded in this cycle yet.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
